import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Briefcase, Building, MapPin, Star, ExternalLink, Users, Calendar, FolderKanban, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Separator } from '@/components/ui/separator';
import { createNotification } from '@/lib/notifications';
import { useToast } from '@/hooks/use-toast';
import JobApplicationForm from './job-application-form';
import InterviewForm from '@/components/interviews/interview-form';

interface JobListProps {
  jobs: any[];
  loading: boolean;
  showMatchPercentage?: boolean;
  emptyMessage?: string;
}

export default function JobList({ jobs, loading, showMatchPercentage = false, emptyMessage }: JobListProps) {
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const { user, currentMode, hasLicense } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [applicationFormOpen, setApplicationFormOpen] = useState(false);
  const [interviewFormOpen, setInterviewFormOpen] = useState(false);
  const [appliedJobs, setAppliedJobs] = useState<Record<string, boolean>>({});
  const [loadingApplications, setLoadingApplications] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUserApplications();
    }
  }, [user]);

  const fetchUserApplications = async () => {
    if (!user) return;
    
    setLoadingApplications(true);
    try {
      const { data, error } = await supabase
        .from('applications')
        .select('job_id')
        .eq('profile_id', user.id);
        
      if (error) {
        console.error('Error fetching user applications:', error);
        return;
      }
      
      // Create a map of job_id -> true for quick lookup
      const appliedJobsMap = data.reduce((acc: Record<string, boolean>, app) => {
        acc[app.job_id] = true;
        return acc;
      }, {});
      
      setAppliedJobs(appliedJobsMap);
    } catch (error) {
      console.error('Error fetching user applications:', error);
    } finally {
      setLoadingApplications(false);
    }
  };

  const toggleJobExpand = (jobId: string) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
    } else {
      setExpandedJobId(jobId);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No deadline';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  const getRemoteOptionLabel = (option: string) => {
    switch (option) {
      case 'onsite': return 'On-site';
      case 'hybrid': return 'Hybrid';
      case 'remote': return 'Remote';
      default: return option;
    }
  };

  const handleApply = (job: any) => {
    if (!user) {
      toast({
        title: "Sign In Required",
        description: "Please sign in to apply for this job",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    
    // Open application dialog
    setSelectedJob(job);
    setApplicationFormOpen(true);
  };

  const handleScheduleInterview = (job: any) => {
    if (!user) {
      toast({
        title: "Sign In Required",
        description: "Please sign in to schedule an interview",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }

    setSelectedJob(job);
    setInterviewFormOpen(true);
  };
  
  const handleViewApplicants = async (job: any) => {
    if (!user) {
      toast({
        title: "Sign In Required",
        description: "Please sign in to view applicants",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    
    // Check if user has recruiter license
    if (currentMode !== 'hire' || !hasLicense('hire')) {
      toast({
        title: "Recruiter License Required",
        description: "You need a recruiter license to view applicants. Please upgrade your account.",
        variant: "destructive"
      });
      navigate('/pricing');
      return;
    }
    
    // Check if user is the job owner
    if (job.profile_id !== user.id) {
      toast({
        title: "Access Denied",
        description: "You can only view applicants for jobs you've posted.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // Create a notification for the job creator
      await createNotification({
        profileId: job.profile_id,  // Send notification to the job creator
        type: 'opportunity',
        title: 'Application View',
        description: `Someone viewed the applicants for your "${job.title}" position`,
        actionUrl: `/dashboard/applications/${job.id}`,
        relatedEntityId: job.id,
        relatedEntityType: 'job_application_view'
      });
      
      // Navigate to the applicants page
      navigate(`/dashboard/applications/${job.id}`, { state: { jobTitle: job.title } });
    } catch (error) {
      console.error('Error handling applicant view:', error);
      toast({
        title: "Error",
        description: "Could not process your request. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Function to check if the current user is the job owner
  const isJobOwner = (job: any) => {
    return user && job.profile_id === user.id;
  };

  // Handle successful application submission
  const handleApplicationSubmitted = () => {
    if (selectedJob) {
      // Update the applied jobs state
      setAppliedJobs(prev => ({
        ...prev,
        [selectedJob.id]: true
      }));
    }
  };

  // Check if user has applied to a specific job
  const hasAppliedToJob = (jobId: string) => {
    return appliedJobs[jobId] || false;
  };

  if (loading || loadingApplications) {
    return (
      <div className="flex justify-center p-6">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-muted-foreground">{emptyMessage || 'No jobs available.'}</div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {jobs.map((job) => (
          <Card 
            key={job.id} 
            className="overflow-hidden"
          >
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{job.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{job.profiles?.company_name} • {job.department}</p>
                  <div className="flex items-center gap-4 mt-1 text-sm">
                    <div className="flex items-center gap-1">
                      <Building className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{job.profiles?.company_name}</span>
                    </div>
                    {job.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{job.location}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>Created {format(new Date(job.created_at), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm">
                    Schedule Interview
                  </Button>
                  <Button>
                    View Profile
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Application Form Dialog */}
      {selectedJob && (
        <JobApplicationForm 
          open={applicationFormOpen}
          onOpenChange={setApplicationFormOpen}
          job={selectedJob}
          onApplicationSubmitted={handleApplicationSubmitted}
        />
      )}

      {/* Interview Form Dialog */}
      {selectedJob && (
        <InterviewForm
          open={interviewFormOpen}
          onOpenChange={setInterviewFormOpen}
          job={selectedJob}
          applicantMode={true}
          onSuccess={() => {
            setInterviewFormOpen(false);
            toast({
              title: 'Success',
              description: 'Interview request submitted successfully',
            });
          }}
        />
      )}
    </>
  );
}